import React, { useEffect, useMemo, useState } from "react";
import { fetchMatkaMonthlyResults } from "../../../api/services/matka";
import { formatDisplayDate, formatResultLine, todayIst } from "./MatkaDailyResults";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

function readError(error) {
  return error?.response?.data?.message || "Unable to load results.";
}

function currentIstMonthYear() {
  const [year, month] = todayIst().split("-");
  return { month: Number(month), year: Number(year) };
}

export default function MatkaMonthlyResults({ markets = [], marketsLoading = false }) {
  const initial = currentIstMonthYear();
  const [marketId, setMarketId] = useState("");
  const [month, setMonth] = useState(initial.month);
  const [year, setYear] = useState(initial.year);
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const years = useMemo(() => {
    const current = currentIstMonthYear().year;
    return [current, current - 1, current - 2, current - 3];
  }, []);

  const resolvedMarketId = marketId || (markets[0] ? String(markets[0].marketId) : "");

  useEffect(() => {
    if (!resolvedMarketId) {
      setLoading(false);
      return undefined;
    }

    let active = true;
    setLoading(true);
    setError("");

    fetchMatkaMonthlyResults(resolvedMarketId, month, year)
      .then((data) => {
        if (active) setPayload(data);
      })
      .catch((err) => {
        if (!active) return;
        setPayload(null);
        setError(readError(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [resolvedMarketId, month, year]);

  const results = Array.isArray(payload?.results) ? payload.results : [];
  const marketName = payload?.market?.name || markets.find((market) => String(market.marketId) === String(resolvedMarketId))?.name || "";
  const heading = [marketName, MONTHS[month - 1], year].filter(Boolean).join(" · ");

  return (
    <div className="matka-results-panel">
      <div className="matka-results-toolbar">
        <div className="matka-results-field">
          <label className="matka-panna-bulk-label" htmlFor="matka-monthly-market">
            Market
          </label>
          <div className="matka-panna-bulk-select-box">
            <select
              id="matka-monthly-market"
              className="matka-panna-bulk-select"
              value={resolvedMarketId}
              onChange={(event) => setMarketId(event.target.value)}
            >
              {markets.map((market) => (
                <option key={market.marketId} value={market.marketId}>
                  {market.name}
                </option>
              ))}
            </select>
            <i className="bi bi-chevron-down matka-panna-bulk-arrow"></i>
          </div>
        </div>

        <div className="matka-results-field">
          <label className="matka-panna-bulk-label" htmlFor="matka-monthly-month">
            Month
          </label>
          <div className="matka-panna-bulk-select-box">
            <select
              id="matka-monthly-month"
              className="matka-panna-bulk-select"
              value={month}
              onChange={(event) => setMonth(Number(event.target.value))}
            >
              {MONTHS.map((name, index) => (
                <option key={name} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
            <i className="bi bi-chevron-down matka-panna-bulk-arrow"></i>
          </div>
        </div>

        <div className="matka-results-field">
          <label className="matka-panna-bulk-label" htmlFor="matka-monthly-year">
            Year
          </label>
          <div className="matka-panna-bulk-select-box">
            <select
              id="matka-monthly-year"
              className="matka-panna-bulk-select"
              value={year}
              onChange={(event) => setYear(Number(event.target.value))}
            >
              {years.map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <i className="bi bi-chevron-down matka-panna-bulk-arrow"></i>
          </div>
        </div>
      </div>

      {marketsLoading && !markets.length ? (
        <div className="matka-empty-state text-center py-5">
          <p className="text-light fs-5">Loading results...</p>
        </div>
      ) : !markets.length ? (
        <div className="matka-empty-state text-center py-5">
          <p className="text-light fs-5">No markets available.</p>
        </div>
      ) : loading ? (
        <div className="matka-empty-state text-center py-5">
          <p className="text-light fs-5">Loading results...</p>
        </div>
      ) : error ? (
        <div className="matka-empty-state text-center py-5">
          <p className="matka-sangam-error fs-5">{error}</p>
        </div>
      ) : (
        <div className="matka-bids-list-card">
          <h3 className="matka-bids-list-title">{heading}</h3>
          {results.length ? (
            <div className="matka-results-table-wrap">
              <table className="matka-bids-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Open Pana</th>
                    <th>Open</th>
                    <th>Close Pana</th>
                    <th>Close</th>
                    <th>Jodi</th>
                    <th>Result</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((row) => {
                    const result = formatResultLine(row);
                    return (
                      <tr key={row.result_date}>
                        <td className="td-panna">{formatDisplayDate(row.result_date)}</td>
                        <td className="td-type">{row.open_pana || "—"}</td>
                        <td className="td-points">{row.open_digit || "—"}</td>
                        <td className="td-type">{row.close_pana || "—"}</td>
                        <td className="td-points">{row.close_digit || "—"}</td>
                        <td className="td-points">{row.jodi_digit || "—"}</td>
                        <td>
                          <span className={`matka-result-line${result.pending ? " is-pending" : ""}`}>
                            {result.text}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="matka-empty-state text-center py-5">
              <p className="text-light fs-5">No results for this month.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
